import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import type { EquipmentListColumn } from "@/components/equipment/equipment-list-columns";
import type { Equipment } from "@/hooks/useEquipment";
import { useEquipmentInstances } from "@/hooks/useEquipmentInstances";
import { cn } from "@/lib/utils";

function EquipmentLinkCell({
  children,
  className,
  to,
}: {
  children: ReactNode;
  className?: string;
  to: string;
}) {
  return (
    <TableCell className={cn("p-0", className)}>
      <Link to={to} className="block h-full p-4 text-inherit no-underline">
        {children}
      </Link>
    </TableCell>
  );
}

const EquipmentRow = ({
  equipment,
  columns,
}: {
  equipment: Equipment;
  columns: EquipmentListColumn[];
}) => {
  const showsStock = columns.some((c) => c.key === "stock");
  const { data: instances = [] } = useEquipmentInstances(
    equipment.is_serialized && showsStock ? equipment.id : undefined,
  );

  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50"
      data-testid={`equipment-row-${equipment.id}`}
    >
      {columns.map((column) => (
        <EquipmentLinkCell
          key={column.key}
          to={`/equipment/${equipment.id}`}
          className={column.className}
        >
          {column.render(equipment, { instancesCount: instances.length })}
        </EquipmentLinkCell>
      ))}
    </TableRow>
  );
};

interface EquipmentTableProps {
  items: Equipment[];
  columns: EquipmentListColumn[];
  isLoading: boolean;
  isFetching?: boolean;
  emptyMessage?: string;
}

export function EquipmentTable({
  items,
  columns,
  isLoading,
  isFetching,
  emptyMessage = "No equipment found",
}: EquipmentTableProps) {
  return (
    <div
      className={`border rounded-lg overflow-hidden transition-opacity ${isFetching && !isLoading ? "opacity-60" : ""}`}
    >
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column.key}>{column.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center py-8">
                Loading equipment...
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center py-8">
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <Package className="h-8 w-8" />
                  <p>{emptyMessage}</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <EquipmentRow key={item.id} equipment={item} columns={columns} />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function getPageNumbers(
  current: number,
  total: number,
): (number | "ellipsis")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "ellipsis")[] = [1];
  if (current > 3) pages.push("ellipsis");
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push("ellipsis");
  pages.push(total);
  return pages;
}

interface EquipmentPaginationProps {
  page: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

export function EquipmentPagination({
  page,
  totalPages,
  totalCount,
  onPageChange,
}: EquipmentPaginationProps) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        {totalCount} item{totalCount !== 1 ? "s" : ""} total
      </p>
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => onPageChange(Math.max(1, page - 1))}
              aria-disabled={page === 1}
              className={
                page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
              }
            />
          </PaginationItem>
          {getPageNumbers(page, totalPages).map((p, i) =>
            p === "ellipsis" ? (
              <PaginationItem key={`e-${i}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={p}>
                <PaginationLink
                  isActive={p === page}
                  onClick={() => onPageChange(p)}
                  className="cursor-pointer"
                >
                  {p}
                </PaginationLink>
              </PaginationItem>
            ),
          )}
          <PaginationItem>
            <PaginationNext
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              aria-disabled={page === totalPages}
              className={
                page === totalPages
                  ? "pointer-events-none opacity-50"
                  : "cursor-pointer"
              }
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
